import PrimeReact from "primereact/api";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from 'primereact/toast';
import { classNames } from "primereact/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Field, Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { LOOKUPS_KEY } from "../../../../../constants/localStorageKeys";
import { CategoryEnum } from "../../../Lookup/enums";
import { Job, JobStatusEnum } from "../../interface";
import { AppDispatch } from "../../../../../store";
import { Link, useParams } from "react-router-dom";
import { createJob, populateSkills, retrieveJob, updateJob } from "../../JobAction";
import LinkButton from "../../../../../constants/helpers/LinkButton";
import { Chips } from "primereact/chips";
import { Chip } from "primereact/chip";
import { useLookupOptions } from "../../../../../constants/helpers/useLookupOptions";
import { reset } from "../../JobSlicer";
import FieldChange from "../../../../../utils/FieldChange";

interface JobFormProps {
  submitButtonLabel: string;
  editable: boolean;
  resettable?: boolean;
}

const JobForm = (props: JobFormProps) => {
  const { submitButtonLabel, resettable = true, editable = false } = props

  PrimeReact.ripple = true
  const toast = useRef<any>(null)
  const dispatch = useDispatch<AppDispatch>()

  const { jobId } = useParams<{ jobId: string }>()
  const { job } = useSelector((state: any) => state.job)

  const [minSalary, setMinSalary] = useState<number | null>(job ? job.minSalary : 0)
  const [maxSalary, setMaxSalary] = useState<number | null>(job ? job.maxSalary : 0)
  const [yearsOfExp, setYearsOfExp] = useState<number | null>(job ? job.yearsOfExp : 0)
  const [maxRetries, setMaxRetries] = useState<number | null>(job ? job.maxRetries : 0)

  const [populateSkillsLoading, setPopulateSkillsLoading] = useState(false);
  const [lastPopulated, setLastPopulated] = useState<{ description?: string; requirements?: string } | null>(null);

  const {
    specializationList, skillList, employmentTypeList,
    languageList, contractTypeList, countryList, qualificationList,
    careerLevelList
  } = useLookupOptions()

  useEffect(() => {
    // load job from db
    if (jobId)
      dispatch(retrieveJob(Number(jobId)))

    return () => {
      dispatch(reset())
    }
  }, [])

  useEffect(() => {
    // set inputnumber values
    if (job && jobId) {
      setMinSalary(job.minSalary)
      setMaxSalary(job.maxSalary)
      setYearsOfExp(job.yearsOfExp)
      setMaxRetries(job.maxRetries)
    }
  }, [job])

  const initialValues: Job = useMemo(() => (job && jobId ?
    {
      id: job.id,
      title: job.title,
      specialization: job.specialization,
      careerLevels: job.careerLevels,
      employmentTypes: job.employmentTypes,
      qualifications: job.qualifications,
      skills: job.skills,
      yearsOfExp: job.yearsOfExp,
      contractType: job.contractType,
      minSalary: job.minSalary,
      maxSalary: job.maxSalary,
      location: job.location,
      languages: job.languages,
      responsibilities: job.responsibilities,
      scopes: job.scopes,
      area: job.area,
      description: job.description,
      requirements: job.requirements,
      status: job.status,
      interviewable: job.interviewable,
      maxRetries: job.maxRetries
    } :
    {
      title: '',
      specialization: '',
      careerLevels: [],
      employmentTypes: [],
      qualifications: [],
      skills: null,
      yearsOfExp: 0,
      contractType: '',
      minSalary: 0,
      maxSalary: 0,
      location: '',
      languages: null,
      responsibilities: '',
      scopes: '',
      area: '',
      description: '',
      requirements: '',
      status: JobStatusEnum.AVAILABLE,
      interviewable: false,
      maxRetries: 0
    }), [job, jobId])

  useEffect(() => {
    // When initialValues are calculated, set the lastPopulated state
    // to match them. This ensures the button is disabled on load.
    setLastPopulated({
      description: initialValues.description,
      requirements: initialValues.requirements,
    });
  }, [initialValues]);

  const statuses = Object.keys(JobStatusEnum).filter(k => isNaN(Number(k)));
  const statusModel = statuses.map(status => ({ label: status, value: status }))

  const validate = (data: any) => {
    let errors = {} as any

    if (!data.title) {
      errors.title = 'Job title is required.'
    }

    if (!data.employmentTypes || (!!data.employmentTypes && data.employmentTypes.length === 0)) {
      errors.employmentTypes = 'Please select at least one employment type.'
    }

    if (!data.minSalary || data.minSalary <= 0) {
      errors.minSalary = 'Minimum salary cannot be less than or equals to 0.'
    }

    if (data.maxSalary < data.minSalary) {
      errors.maxSalary = 'Maximum salary cannot be less than minimum salary.'
    }

    if (!data.specialization) {
      errors.specialization = 'Please select a specialization.'
    }

    if (!data.description) {
      errors.description = 'Please enter a description.'
    }

    if (!data.skills || (!!data.skills && data.skills.length === 0)) {
      errors.skills = 'Please enter at least one skill.'
    }

    if (!data.qualifications || (!!data.qualifications && data.qualifications.length === 0)) {
      errors.qualifications = 'Please select at least one qualification.'
    }

    if (!data.careerLevels || (!!data.careerLevels && data.careerLevels.length === 0)) {
      errors.careerLevels = 'Please select at least one career level.'
    }

    if (!data.languages || (!!data.languages && data.languages.length === 0)) {
      errors.languages = 'Please select at least one language.'
    }

    if (!data.location) {
      errors.location = 'Please select a location.'
    }

    if ((!data.maxRetries || data.maxRetries.value === 0) && data.interviewable) {
      errors.maxRetries = 'Maximum number of allowable retries cannot be 0.'
    }

    return errors;
  }

  const onSubmit = (data: any, form: any) => {
    let payload = {} as Job
    payload.id = data.id
    payload.title = data.title
    payload.specialization = data.specialization
    payload.careerLevels = data.careerLevels
    payload.employmentTypes = data.employmentTypes
    payload.qualifications = data.qualifications
    payload.skills = data.skills
    payload.yearsOfExp = !!data.yearsOfExp.value ? data.yearsOfExp.value : yearsOfExp
    payload.contractType = data.contractType
    payload.minSalary = !!data.minSalary.value ? data.minSalary.value : minSalary
    payload.maxSalary = !!data.maxSalary.value ? data.maxSalary.value : maxSalary
    payload.location = data.location
    payload.languages = data.languages
    payload.responsibilities = data.responsibilities
    payload.scopes = data.scopes
    payload.area = data.area
    payload.description = data.description
    payload.requirements = data.requirements
    payload.status = data.status
    payload.interviewable = data.interviewable
    payload.maxRetries = data.interviewable ? (!!data.maxRetries.value ? data.maxRetries.value : maxRetries) : 0

    if (jobId) {
      dispatch(updateJob(payload)).unwrap()
        .then(() => {
          toast.current.show({
            severity: 'success',
            summary: 'Job Update Success',
            detail: `Job (${payload.title}) successfully updated.`,
            life: 3000
          })
        })
        .catch(() => {
          toast.current.show({
            severity: 'warn',
            summary: 'Job Update Error',
            detail: `Job (${payload.title}) update failed.`,
            life: 3000
          })
        })
    } else {
      dispatch(createJob(payload)).unwrap()
        .then(() => {
          toast.current.show({
            severity: 'success',
            summary: 'Job Create Success',
            detail: `Job (${payload.title}) successfully created.`,
            life: 3000
          })
          form.restart()
          setMinSalary(0)
          setMaxSalary(0)
          setYearsOfExp(0)
          setMaxRetries(0)
        })
        .catch(() => {
          toast.current.show({
            severity: 'warn',
            summary: 'Job Create Error',
            detail: `Job (${payload.title}) creation failed.`,
            life: 3000
          })
        })
    }
  }

  const handleReset = (form: any) => {
    console.log('form:', form)
    form.restart()

    // reset inputnumbers
    if (job && jobId) {
      setMinSalary(job.minSalary)
      setMaxSalary(job.maxSalary)
      setYearsOfExp(job.yearsOfExp)
      setMaxRetries(job.maxRetries)
    } else {
      setMinSalary(0)
      setMaxSalary(0)
      setYearsOfExp(0)
      setMaxRetries(0)
    }
    toast.current.show({ severity: 'info', summary: 'Form Reset', detail: 'Form details have been cleared', life: 3000 })
  }

  const isFormFieldValid = (meta: any) => !!(meta.touched && meta.error)
  const getFormErrorMessage = (meta: any) => {
    return isFormFieldValid(meta) && <small className="p-error">{meta.error}</small>
  }

  const onPopulateSkills = useCallback((form: any, job: Job) => {
    setPopulateSkillsLoading(true)
    console.log('job', job)
    
    dispatch(populateSkills(job)).unwrap()
      .then((data) => {
        form.change('skills', data.skills);
        toast.current.show({
          severity: 'success', 
          summary: 'Skills Populated successfully.'
        })
        setLastPopulated({ description: job.description, requirements: job.requirements });
      })
      .finally(() => {
        setPopulateSkillsLoading(false)
      });
  }, [])

  return (
    <div>
      <Toast ref={toast} />
      <Form onSubmit={onSubmit} initialValues={initialValues} validate={validate} render={({ form, handleSubmit, values, pristine, dirty, dirtyFields }) => (
        <form onSubmit={handleSubmit}>
          <div className="formgrid grid align-self-center mx-2 my-3">
            {jobId && (
              <div className="field col-12">
                {!editable ? (
                  <>
                    <LinkButton className="p-button-primary" to={"/jobs/" + jobId + "/update"} icon="eye" label="Edit" />
                    <LinkButton className="ml-2" to={"/jobs/" + jobId + "/setup"} icon="cog" label="Setup Interview Flow" />
                    <LinkButton className="ml-2" to={"/jobs/" + jobId + "/match"} icon="sparkles" label="Match Resumes to this Job" />
                    <LinkButton className="ml-2" to={"/jobs/" + jobId + "/interviews"} icon="video" label="View Interviews for this Job" />
                  </>
                ) : (
                  <LinkButton className="p-button-primary" to={"/jobs/" + jobId} icon="arrow-left" label="Back" />
                )}
              </div>
            )}

            <div className="col-12">
              <Card title="Job Information" className="my-3">
                <div className="grid align-self-center">
                  <Field name="title" render={({ input, meta }) => (
                    <div className="field col-12 md:col-6">
                      <label htmlFor="titleInput" className={classNames({ 'p-error': isFormFieldValid(meta) })}>Job Title*</label>
                      {editable ? (
                        <InputText id="titleInput" {...input} autoFocus type="text" className={classNames("inputfield w-full", { 'p-invalid': isFormFieldValid(meta) })} />
                      ) : (
                        <div>{job.title}</div>
                      )}
                      {getFormErrorMessage(meta)}
                    </div>
                  )} />
                  <Field name="employmentTypes" render={({ input, meta }) => (
                    <div className="field col-12 md:col-6">
                      <label htmlFor="employmentTypesPicker" className={classNames({ 'p-error': isFormFieldValid(meta) })}>Employment Type*</label>
                      {editable ? (
                        <MultiSelect id="employmentTypesPicker" {...input} filter className={classNames("inputfield w-full", { 'p-error': isFormFieldValid(meta) })} options={employmentTypeList} showClear placeholder="Select Employment Type" />
                      ) : (
                        <div className="font-medium" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {Array.isArray(job.employmentTypes) && job.employmentTypes.length > 0
                            ? job.employmentTypes.map((q: string, idx: number) => (
                              <Chip key={idx} label={q} />
                            ))
                            : '-'}
                        </div>
                      )}
                      {getFormErrorMessage(meta)}
                    </div>
                  )} />
                  <Field name="contractType" render={({ input, meta }) => (
                    <div className="field col-12 md:col-6">
                      <label htmlFor="contractTypePicker" className={classNames({ 'p-error': isFormFieldValid(meta) })}>Contract Type</label>
                      {editable ? (
                        <Dropdown id="contractTypePicker" {...input} filter className={classNames("inputfield w-full", { 'p-error': isFormFieldValid(meta) })} options={contractTypeList} placeholder="Select a Contract Type" />
                      ) : (
                        <div>{job.contractType}</div>
                      )}
                      {getFormErrorMessage(meta)}
                    </div>
                  )} />
                  <Field name="specialization" render={({ input, meta }) => (
                    <div className="field col-12 md:col-6">
                      <label htmlFor="specializationInput" className={classNames({ 'p-error': isFormFieldValid(meta) })}>Specialization*</label>
                      {editable ? (
                        <Dropdown id="specializationInput" {...input} filter virtualScrollerOptions={{ itemSize: 38 }} className={classNames("inputfield w-full", { 'p-error': isFormFieldValid(meta) })} options={specializationList} placeholder="Select a Specialization" />
                      ) : (
                        <div>{job.specialization}</div>
                      )}
                      {getFormErrorMessage(meta)}
                    </div>
                  )} />
                  <Field name="description" render={({ input, meta }) => (
                    <div className="field col-12">
                      <label htmlFor="descriptionInput" className={classNames({ 'p-error': isFormFieldValid(meta) })}>Job Description*</label>
                      {editable ? (
                        <InputTextarea id="descriptionInput" {...input} className={classNames("inputfield w-full", { 'p-error': isFormFieldValid(meta) })} rows={6} />
                      ) : (
                        <div style={{ whiteSpace: 'pre-wrap' }}>{job.description}</div>
                      )}
                      {getFormErrorMessage(meta)}
                    </div>
                  )} />
                  <Field name="minSalary" render={({ input, meta }) => (
                    <div className="field col-12 md:col-6">
                      <label htmlFor="minSalary" className={classNames({ 'p-error': isFormFieldValid(meta) })}>Minimum Salary*</label>
                      {editable ? (
                        <InputNumber 
                          id="minSalary"
                          {...input}
                          value={minSalary}
                          className={classNames("inputfield w-full", { 'p-invalid': isFormFieldValid(meta) })}
                          onChange={(e) => FieldChange(setMinSalary, input, e.value || 0)}
                          onValueChange={(e) => FieldChange(setMinSalary, input, e.value || 0)}
                          prefix="$"
                          showButtons
                          mode="currency"
                          currency="SGD"
                          min={0} />
                      ) : (
                        <div>SGD {job.minSalary && job.minSalary.toLocaleString()}</div>
                      )}
                      {getFormErrorMessage(meta)}
                    </div>
                  )} />
                  <Field name="maxSalary" render={({ input, meta }) => (
                    <div className="field col-12 md:col-6">
                      <label htmlFor="maxSalary" className={classNames({ 'p-error': isFormFieldValid(meta) })}>Maximum Salary*</label>
                      {editable ? (
                        <InputNumber
                          id="maxSalary"
                          {...input}
                          value={maxSalary}
                          className={classNames("inputfield w-full", { 'p-error': isFormFieldValid(meta) })}
                          onChange={(e) => FieldChange(setMaxSalary, input, e.value || 0)}
                          onValueChange={(e) => FieldChange(setMaxSalary, input, e.value || 0)}
                          prefix="$"
                          showButtons
                          mode="currency"
                          currency="SGD"
                          min={0} />
                      ) : (
                        <div>SGD {job.maxSalary && job.maxSalary.toLocaleString()}</div>
                      )}
                      {getFormErrorMessage(meta)}
                    </div>
                  )} />
                </div>
              </Card>
            </div>
            <div className="col-12">
              <Card title="Desired Skills and Qualifications" className="my-3">
                <div className="grid align-self-center">
                  <Field name="requirements" render={({ input }) => (
                    <div className="field col-12">
                      <label htmlFor="requirements">Job Requirements</label>
                      {editable ? (
                        <InputTextarea id="requirements" {...input} className="inputfield w-full" rows={4} />
                      ) : (
                        <div style={{ whiteSpace: 'pre-wrap' }}>{job.requirements || '-'}</div>
                      )}
                    </div>
                  )} />
                  <Field name="skills" render={({ input, meta }) => (
                    <div className="field col-12">
                      <label htmlFor="skillPicker" className={classNames({ 'p-error': isFormFieldValid(meta) })}>Skills*</label>
                      {editable ? (
                        <div className="p-inputgroup flex-1">
                          <Chips id="skillPicker" {...input} className={classNames("inputfield w-full", { 'p-error': isFormFieldValid(meta) })} separator="," placeholder="Enter Skill(s)" />
                          <Button 
                            type="button"
                            icon="pi pi-bolt"
                            loading={populateSkillsLoading}
                            onTouchEnd={(e) => onPopulateSkills(form, values)}
                            onMouseDown={(e) => onPopulateSkills(form, values)}
                            disabled={
                              populateSkillsLoading ||
                              !values.description ||
                              !values.requirements ||
                              (lastPopulated?.description === values.description &&
                                lastPopulated?.requirements === values.requirements)
                            }
                            tooltip="Auto-populate Skills!"
                            tooltipOptions={{ position: 'top' }}
                          />
                        </div>
                      ) : (
                        <div className="font-medium" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {Array.isArray(job.skills) && job.skills.length > 0
                            ? job.skills.map((s: string, idx: number) => (
                              <Chip key={idx} label={s} />
                            ))
                            : '-'}
                        </div>
                      )}
                      {getFormErrorMessage(meta)}
                    </div>
                  )} />
                  <Field name="yearsOfExp" render={({ input }) => (
                    <div className="field col-12 md:col-6">
                        <label htmlFor="yearsOfExp">Years of Experience</label>
                        {editable ? (
                        <InputNumber
                          id="yearsOfExp"
                          {...input}
                          className="inputfield w-full"
                          suffix=" year(s)"
                          showButtons
                          value={yearsOfExp}
                          onChange={(e) => FieldChange(setYearsOfExp, input, e.value || 0)}
                          onValueChange={(e) => FieldChange(setYearsOfExp, input, e.value || 0)}
                          min={0}
                          max={100}
                        />
                        ) : (
                        <div>{job.yearsOfExp || '-'}</div>
                      )}
                    </div>
                  )} />
                  <Field name="qualifications" render={({ input, meta }) => (
                    <div className="field col-12 md:col-6">
                      <label htmlFor="qualificationPicker" className={classNames({ 'p-error': isFormFieldValid(meta) })}>Qualifications*</label>
                      {editable ? (
                        <MultiSelect id="qualificationPicker" {...input} filter className={classNames("inputfield w-full", { 'p-error': isFormFieldValid(meta) })} options={qualificationList} showClear placeholder="Select a Qualification Type" />
                      ) : (
                        <div className="font-medium" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {Array.isArray(job.qualifications) && job.qualifications.length > 0
                            ? job.qualifications.map((q: string, idx: number) => (
                              <Chip key={idx} label={q} />
                            ))
                            : '-'}
                        </div>
                      )}
                      {getFormErrorMessage(meta)}
                    </div>
                  )} />
                </div>
              </Card>
            </div>
            <div className="col-12">
              <Card title="Additional Details" className="my-3">
                <div className="grid align-self-center">
                  <Field name="responsibilities" render={({ input }) => (
                    <div className="field col-12 md:col-6">
                      <label htmlFor="responsibilities">Responsibilities</label>
                      {editable ? (
                        <InputTextarea id="responsibilities" {...input} className="inputfield w-full" rows={4} />
                      ) : (
                        <div style={{ whiteSpace: 'pre-wrap' }}>{job.responsibilities || '-'}</div>
                      )}
                    </div>
                  )} />
                  <Field name="scopes" render={({ input }) => (
                    <div className="field col-12 md:col-6">
                      <label htmlFor="scopes">Scopes</label>
                      {editable ? (
                        <InputTextarea id="scopes" {...input} className="inputfield w-full" rows={4} />
                      ) : (
                        <div>{job.scopes || '-'}</div>
                      )}
                    </div>
                  )} />
                  <Field name="careerLevels" render={({ input, meta }) => (
                    <div className="field col-12 md:col-6">
                      <label htmlFor="careerPicker" className={classNames({ 'p-error': isFormFieldValid(meta) })}>Career Level*</label>
                      {editable ? (
                        <MultiSelect id="careerPicker" {...input} filter className={classNames("inputfield w-full", { 'p-error': isFormFieldValid(meta) })} options={careerLevelList} showClear placeholder="Select Career Level(s)" />
                      ) : (
                        <div className="font-medium" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {Array.isArray(job.careerLevels) && job.careerLevels.length > 0
                            ? job.careerLevels.map((level: string, idx: number) => (
                              <Chip key={idx} label={level} />
                            ))
                            : '-'}
                        </div>
                      )}
                      {getFormErrorMessage(meta)}
                    </div>
                  )} />
                  <Field name="languages" render={({ input, meta }) => (
                    <div className="field col-12 md:col-6">
                      <label htmlFor="languagePicker" className={classNames({ 'p-error': isFormFieldValid(meta) })}>Language(s)*</label>
                      {editable ? (
                        <MultiSelect id="languagePicker" {...input} filter className={classNames("inputfield w-full", { 'p-error': isFormFieldValid(meta) })} options={languageList} showClear placeholder="Select Language(s)" />
                      ) : (
                        <div className="font-medium" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {Array.isArray(job.languages) && job.languages.length > 0
                            ? job.languages.map((q: string, idx: number) => (
                              <Chip key={idx} label={q} />
                            ))
                            : '-'}
                        </div>
                      )}
                      {getFormErrorMessage(meta)}
                    </div>
                  )} />
                  <Field name="location" render={({ input, meta }) => (
                    <div className="field col-12 md:col-6">
                      <label htmlFor="locationPicker" className={classNames({ 'p-error': isFormFieldValid(meta) })}>Location*</label>
                      {editable ? (
                        <Dropdown id="locationPicker" {...input} filter virtualScrollerOptions={{ itemSize: 38 }} className={classNames("inputfield w-full", { 'p-error': isFormFieldValid(meta) })} options={countryList} placeholder="Select a Location" />
                      ) : (
                        <div>{job.location}</div>
                      )}
                      {getFormErrorMessage(meta)}
                    </div>
                  )} />
                  <Field name="area" render={({ input }) => (
                    <div className="field col-12 md:col-6">
                      <label htmlFor="area">Area</label>
                      {editable ? (
                        <InputText id="area" {...input} className="field w-full" />
                      ) : (
                        <div>{job.area || '-'}</div>
                      )}
                    </div>
                  )} />
                  <Field name="status" render={({ input, meta }) => (
                    <div className="field col-12 md:col-6">
                      <label htmlFor="status">Status</label>
                      {editable ? (
                        <Dropdown id="status" {...input} filter className={classNames("inputfield w-full", { 'p-error': isFormFieldValid(meta) })} options={statusModel} placeholder="Select a Status" />
                      ) : (
                        <div>{job.status}</div>
                      )}
                    </div>
                  )} />

                  <div className="field col-12 md:col-3">
                    <label>Chat Interview</label>
                    {editable ? (
                      <div className="p-inputgroup flex-1">
                        <span className="p-inputgroup-addon">
                          <Field name="interviewable" type="checkbox" render={({ input }) => (
                            <Checkbox inputId="interviewable" {...input} checked={input.checked || false} id="interviewable" />
                          )} />
                        </span>
                        <Field name="maxRetries" render={({ input, meta }) => (
                          <InputNumber id="maxRetries" {...input}
                            className={classNames("inputfield w-full", { 'p-error': isFormFieldValid(meta) })}
                            min={0}
                            suffix=" maximum attempt(s)"
                            required={values.interviewable}
                            disabled={!values.interviewable}
                            showButtons value={maxRetries}
                            onChange={(e) => FieldChange(setMaxRetries, input, e.value || 0)}
                            onValueChange={(e) => FieldChange(setMaxRetries, input, e.value || 0)} />
                        )} />
                      </div>
                    ) : (
                      <div>{job.interviewable ? (
                        <>
                          <span>Enabled</span> - <span>{job.maxRetries} max attempts</span>
                        </>
                      ) : 'Disabled'}</div>
                    )}
                  </div>
                </div>
              </Card>
            </div>
            <div className="col-12">
              {editable && (
                <div className="flex justify-content-end align-items-end">
                  {resettable && <Button className="p-button-outlined p-button-secondary mr-3" label="Reset" icon="pi pi-refresh" onClick={() => handleReset(form)} disabled={pristine}></Button>}
                  <Button type="submit" label={submitButtonLabel} icon="pi pi-check"></Button>
                </div>
              )}
            </div>
          </div>
        </form>
      )} />
    </div>
  );
};

export default JobForm;